import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  styled,
  Typography,
  Paper,
  Link,
  IconButton,
  Chip,
  Menu,
  MenuItem,
} from '@mui/material';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import ArrowBackIos from '@mui/icons-material/ArrowBackIos';
import { useNavigate } from 'react-router-dom';
import TextSearch from '../../controls/TextSearch';
import SearchIcon from '@mui/icons-material/Search';
import {
  DataGridPro,
  GridCallbackDetails,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
  GridSortItem,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import * as lookupSlice from '../../../data/lookup.slice';
import { CellActions } from '../../tables/DataGridPro';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { SortModel } from '../../../data/models/sort-model.model';
import { PurposeCodeModel } from '../../../data/models/purpose-code-model.model';
import { PagedList } from '../../../data/models/paged-list.model';
import PurposeCodeModelDialog from './PurposeCodeModelDialog';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'PurposeCodes';

export const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  previous: `${PREFIX}-previous`,
  title: `${PREFIX}-title`,
  actions: `${PREFIX}-actions`,
  search: `${PREFIX}-search`,
  chip: `${PREFIX}-chip`,
  inactive: `${PREFIX}-inactive`,
};

const StyledBox = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    margin: theme.spacing(3),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    flexDirection: 'column',
  },
  [`& .${classes.previous}`]: {
    display: 'flex',
    fontFamily: 'Source Sans Pro',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: 14,
    lineHeight: '157%',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    color: '#197CB3',
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
    lineHeight: theme.spacing(5),
    letterSpacing: '0.25px',
    color: 'rgba(1, 16, 18, 0.9)',
  },
  [`& .${classes.actions}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  [`& .${classes.search}`]: {
    width: 266,
  },
  [`& .${classes.chip}`]: {
    width: 80,
    backgroundColor: 'rgba(2, 136, 209, 0.12)',
    '& .MuiChip-label': {
      color: '#01579B',
      fontFamily: 'Source Sans Pro',
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: 600,
      letterSpacing: '0.14px',
    },
  },
  [`& .${classes.inactive}`]: {
    width: 80,
    backgroundColor: 'rgba(1, 16, 18, 0.08)',
    '& .MuiChip-label': {
      color: 'rgba(1, 16, 18, 0.90)',
    },
  },
}));

export function PurposeCodes() {
  const dispatch = useAppDispatch();
  const t = useCommonTranslation();
  const navigate = useNavigate();
  const isSuperReaderUser = useIsSuperReaderUser();

  const [currentSearchText, setCurrentSearchText] = useState('');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchFilter, setSearchFilter] = useState('');
  const defaultSortItem: GridSortItem = {
    field: 'transportationProviderName',
    sort: 'asc',
  };
  const [sortModel, setSortModel] = useState([defaultSortItem]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPurposeCode, setSelectedPurposeCode] =
    useState<PurposeCodeModel>();
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    dispatch(lookupSlice.fetchLookupItemsAsync('purposeCodeModelType'));
  }, []);
  const purposeCodeModelTypes = useAppSelector(
    lookupSlice.selectPurposeCodeModelTypes,
  );

  const gotoAdmin = () => {
    navigate('/settings');
  };

  const onSearchTextChange = (val: string) => {
    setCurrentSearchText(val);
  };

  const addPurposeCode = () => {
    setDialogOpen(true);
  };

  const handleMore = (evt: React.MouseEvent<HTMLElement>, purposeCode: any) => {
    setMenuAnchorEl(evt.currentTarget);
    setMenuOpen(true);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
    setMenuAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    setIsDuplicate(false);
    setDialogOpen(true);
  };

  const handleDuplicate = () => {
    handleMenuClose();
    setIsDuplicate(true);
    setDialogOpen(true);
  };

  const purposeCodes: PagedList<PurposeCodeModel> = {
    itemCount: 2,
    currentPage: 1,
    totalPages: 1,
    items: [
      {
        purposeCodeID: 1,
        name: 'Dentist',
        purposeCodeModelTypeBC: 'MEDICAL',
        isActive: true,
        servicePlanCount: 4,
      },
      {
        purposeCodeID: 2,
        name: 'Chiropractor',
        purposeCodeModelTypeBC: 'OTHER',
        isActive: false,
        servicePlanCount: 0,
      },
    ],
  };
  const loadingPurposeCodes = false;
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('purposeCodes.name'),
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        return <>{params.row.name}</>;
      },
    },
    {
      field: 'type',
      headerName: t('purposeCodes.type'),
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const t = purposeCodeModelTypes.find(
          (pcmt) =>
            pcmt.purposeCodeModelTypeBC === params.row.purposeCodeModelTypeBC,
        );
        return <>{t?.purposeCodeModelTypeName || ''}</>;
      },
    },
    {
      field: 'servicePlans',
      headerName: t('purposeCodes.servicePlans'),
      width: 100,
      sortable: false,
      disableColumnMenu: true,
      headerAlign: 'right',
      align: 'right',
      renderCell: (params: GridRenderCellParams) => {
        return <>{params.row.servicePlanCount || 0}</>;
      },
    },
    {
      field: 'status',
      headerName: t('purposeCodes.status'),
      width: 90,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => {
        const isActive = params.row.isActive;
        const label = isActive
          ? t('purposeCodes.active')
          : t('purposeCodes.inactive');
        return (
          <Chip
            label={label}
            color="primary"
            className={
              isActive ? classes.chip : `${classes.chip} ${classes.inactive}`
            }
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <>
          {!isSuperReaderUser && (
            <CellActions>
              <IconButton
                size="large"
                onClick={(evt) => handleMore(evt, params.row)}>
                <MoreVertOutlinedIcon />
              </IconButton>
            </CellActions>
          )}
        </>
      ),
    },
  ];

  const paginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    setPageSize(model.pageSize);
    // console.log('paginationChange', model);
    // TODO: this is waiting for back end support for purpose codes
    // if (model.page !== todaysTripLegs.currentPage - 1) {
    //   dispatch(triplegSlice.setTodayPage(model.page + 1));
    // }
    // if (model.pageSize !== pageSize) {
    //   dispatch(triplegSlice.setTodayPageSize(model.pageSize));
    // }
  };

  const sortModelChange = (
    model: GridSortModel,
    details: GridCallbackDetails,
  ) => {
    setPage(1);
    setSortModel(model);
    // console.log('sortModelChange', model, details);
    // TODO: this is waiting for back end support for purpose codes
    // let newModel: SortModel = {
    //       column: model[0].field,
    //       direction: model[0].sort,
    //     };
    //   dispatch(triplegSlice.setTodayPageSortModel(newModel));
  };

  const handleRowClick = (params: GridRowParams) => {
    setSelectedPurposeCode(params.row);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedPurposeCode(undefined);
    setIsDuplicate(false);
  };

  return (
    <StyledBox className={classes.container}>
      <Box className={classes.header}>
        <Link
          color="primary"
          className={classes.previous}
          href="#"
          onClick={gotoAdmin}
          underline="hover">
          <ArrowBackIos fontSize="small" />
          {t('settings.title')}
        </Link>
        <Typography variant="h3" className={classes.title}>
          {t('settings.purposeCodeModels')}
        </Typography>
        <Box className={classes.actions}>
          <TextSearch
            id="text-search"
            icon={<SearchIcon />}
            className={classes.search}
            label={t('common.search')}
            currentSearchFilter={currentSearchText}
            onSearchChange={onSearchTextChange}></TextSearch>
          {!isSuperReaderUser && (
            <Button
              variant="contained"
              color="primary"
              onClick={addPurposeCode}>
              {t('common.add')}
            </Button>
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'grid' }}>
        <DataGridPro
          getRowId={(row) => row.purposeCodeID}
          pagination={true}
          paginationModel={{ page: purposeCodes.currentPage - 1, pageSize }}
          paginationMode="server"
          sortingMode="server"
          pageSizeOptions={[10, 20, 40, 50]}
          rowCount={purposeCodes.itemCount}
          onPaginationModelChange={paginationChange}
          onSortModelChange={sortModelChange}
          onRowClick={handleRowClick}
          rows={purposeCodes.items}
          columns={columns}
          disableColumnSelector={true}
          sortingOrder={['desc', 'asc', null]}
          disableRowSelectionOnClick={true}
          loading={loadingPurposeCodes}
        />
      </Box>
      <PurposeCodeModelDialog
        open={dialogOpen}
        purposeCodeModel={selectedPurposeCode}
        isDuplicate={isDuplicate}
        purposeCodeModelTypes={purposeCodeModelTypes}
        close={closeDialog}
      />
      <Menu anchorEl={menuAnchorEl} open={menuOpen} onClose={handleMenuClose}>
        <MenuItem onClick={handleEdit}>{t('common.edit')}</MenuItem>
        <MenuItem onClick={handleDuplicate}>{t('common.duplicate')}</MenuItem>
      </Menu>
    </StyledBox>
  );
}
